import * as React from 'react'
import { Row, Col, Button, ButtonGroup, FormControl, FormGroup, ControlLabel, Panel } from 'react-bootstrap'
import InstantArticlesItem from './InstantArticlesItem'
import { Article, InstantArticleLimits } from '../../articlesContainer'
import LoadIndicator from '../../../../../common/LoadIndicator'
import IconButton from '../../../../../common/IconButton'
import BodyEditor from './BodyEditor'
import { captureExceptionWithSentry } from '../../../../../sentry'
import { InstantArticle } from './InstantArticles'
import './InstantArticlesModal.css'
import apiFetch from '../../../../../common/apiFetch'

interface InstantArticlesModalProps {
	article: Article
	instantArticles: InstantArticle
	onHide: () => void
	show: boolean
	updateInstantArticle: (values: { [property: string]: string }) => void
	updateInstantArticlesProperties: (values: {
		link_description?: string
		link_url?: string
		logo?: string
		logo_id?: string
		client_name?: string
	}) => void
	client?: {
		id: number
		org: string
	}
}

const InstantArticlesModal = (props: InstantArticlesModalProps) => {
	const [editable, setEditable] = React.useState(false)
	const [focusedElement, setFocusedElement] = React.useState<string | undefined>(undefined)
	const [imageSize, setImageSize] = React.useState<{ height?: number; width?: number }>({})
	const [imageLoading, setImageLoading] = React.useState(false)
	const [imageError, setImageError] = React.useState(false)
	const [logoError, setLogoError] = React.useState(false)
	const [logoLoading, setLogoLoading] = React.useState(false)

	React.useEffect(() => {
		props.updateInstantArticlesProperties({
			client_name: props.client?.org,
		})
	}, [props.client?.org])

	const toggleEditable = (focused?: string) => {
		if (!validateIframe(props.article.instantArticle?.iframe || '')) {
			setFocusedElement('main-iframe')
			return
		}
		setEditable((prevState) => !prevState)
		setFocusedElement(focused)
	}

	const setHeadline = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
		props.updateInstantArticle({ headline: e.currentTarget.value })
	}

	const setIntroduction = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
		props.updateInstantArticle({ introduction: e.currentTarget.value })
	}

	const setVideoUrl = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
		props.updateInstantArticle({ iframe: e.currentTarget.value })
	}

	const setMainImage = (e: any) => {
		setImage(e, 'main')
	}

	const imageUploadCallback = async (e: any) => {
		const url = await setImage(e, 'body')
		return {
			data: {
				link: url,
			},
		}
	}

	const setImage = async (e: any | File, type: 'main' | 'body'): Promise<string> => {
		const file = type === 'main' ? e.target.files[0] : e
		if (type === 'main') {
			e.preventDefault()
			setImageLoading(true)
		}

		if (!file) {
			return ''
		}

		const fileReader = new FileReader()
		fileReader.readAsDataURL(file)
		return new Promise((resolve) => {
			fileReader.addEventListener('loadend', async () => {
				const result = fileReader.result
				if (typeof result !== 'string') {
					return
				}
				switch (type) {
					case 'main':
						await saveMainImage(result)
						return resolve('')
					case 'body':
						resolve((await saveBodyImage(result)) as string)
				}
			})
		})
	}

	const setBody = (html: string) => {
		props.updateInstantArticle({ body: html })
	}

	const saveBodyImage = async (image: string, checkOnly = false) => {
		const onlyCheckImageValidity = checkOnly ? '?validate=true' : ''
		const data = {
			image,
			type: 'instant_article_body',
		}
		try {
			const imageSaveResult = await apiFetch(`/images${onlyCheckImageValidity}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(data),
			})
			if (imageSaveResult.ok) {
				const response = await imageSaveResult.json()
				const images = response.data
				let shownImage = images[0]
				for (const img of images) {
					if (+img.width > +shownImage.width) {
						shownImage = img
					}
					if (+img.width === 1200) {
						break
					}
				}
				return shownImage.path
			}
			return ''
		} catch (error) {
			captureExceptionWithSentry(error)
		}
	}

	const saveMainImage = async (image: string, checkOnly = false) => {
		const onlyCheckImageValidity = checkOnly ? '?validate=true' : ''
		const data = {
			image,
			type: 'instant_article_main',
		}
		try {
			const imageSaveResult = await apiFetch(`/images${onlyCheckImageValidity}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(data),
			})
			if (imageSaveResult.ok) {
				if (checkOnly) {
					props.updateInstantArticle({
						image,
						image_id: '',
					})
					setImageLoading(false)
					return true
				}
				const response = await imageSaveResult.json()
				const images = response.data
				let shownImage = images[0]
				let imageHeight = +shownImage.height
				let imageWidth = +shownImage.width

				const MIN_WIDTH = 1200
				const MIN_HEIGHT = 628
				for (const img of images) {
					if (+img.width > +shownImage.width) {
						shownImage = img
					}
					if (+img.width >= MIN_WIDTH && +img.height >= MIN_HEIGHT) {
						imageHeight = +img.height
						imageWidth = +img.width
						break
					}
				}
				setImageSize({
					height: imageHeight,
					width: imageWidth,
				})
				setImageError(false)

				if (imageWidth >= MIN_WIDTH && imageHeight >= MIN_HEIGHT) {
					props.updateInstantArticle({
						image: shownImage.path,
						image_id: shownImage.id,
					})
				}
			} else {
				props.updateInstantArticle({ image: '' })
				setImageSize({})
				setImageError(true)
			}
		} catch (error) {
			captureExceptionWithSentry(error)
			props.updateInstantArticle({ image: '' })
			setImageSize({})
			setImageError(true)
		}

		setImageLoading(false)
	}

	const saveLogo = async (image: string, checkOnly = false) => {
		const onlyCheckImageValidity = checkOnly ? '?validate=true' : ''
		const data = {
			image,
			type: 'instant_article_logo',
		}
		try {
			const imageSaveResult = await apiFetch(`/images${onlyCheckImageValidity}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(data),
			})
			if (imageSaveResult.ok) {
				if (checkOnly) {
					props.updateInstantArticlesProperties({
						logo: image,
						logo_id: '',
					})
					setLogoLoading(false)
					return true
				}
				const response = await imageSaveResult.json()
				const images = response.data
				if (images && image.length > 0) {
					setLogoError(false)
					let shownImage = images[0]
					// The logo should have width 300
					for (const img of images) {
						if (+img.width === 300) {
							shownImage = img
							break
						}
					}
					props.updateInstantArticlesProperties({
						logo: shownImage.path,
						logo_id: shownImage.id,
					})
				}
			} else {
				props.updateInstantArticlesProperties({ logo: '' })
				setLogoError(true)
			}
		} catch (error) {
			captureExceptionWithSentry(error)
			props.updateInstantArticlesProperties({ logo: '' })
			setLogoError(true)
		}

		setLogoLoading(false)
	}

	const renderImage = () => {
		return (
			<Row>
				<FormGroup
					controlId={`image-${props.article.uid}`}
					validationState={props.article.instantArticle?.image ? 'success' : 'error'}
					style={{ margin: 0 }}
				>
					<Col md={12}>
						<ControlLabel style={{ width: '100%' }}>
							{imageLoading ? (
								<LoadIndicator />
							) : props.article.instantArticle?.image !== '' ? (
								<>
									{props.article.instantArticle!.iframe && (
										<Col
											style={{
												marginTop: '15px',
												marginBottom: '5px',
												color: 'black',
												fontWeight: 0,
												textAlign: 'left',
											}}
										>
											The image is used as a thumbnail image in the more articles column
										</Col>
									)}
									<img
										data-test="variation-image"
										src={props.article.instantArticle?.image}
										width={props.article.instantArticle!.iframe ? '50%' : '100%'}
										onError={() => {
											props.updateInstantArticle({ image: '' })
											setImageSize({
												height: undefined,
												width: undefined,
											})
										}}
										className="img-thumbnail"
										style={{ cursor: 'pointer', border: 0, padding: '0px', float: 'left' }}
										onLoad={(img: any) => {
											if (img.target && !imageSize.height && !imageSize.width) {
												setImageSize({
													height: img.target.naturalHeight,
													width: img.target.naturalWidth,
												})
											}
										}}
									/>
								</>
							) : (
								<>
									{props.article.instantArticle!.iframe && (
										<Col
											style={{
												marginTop: '15px',
												marginBottom: '5px',
												color: 'black',
												fontWeight: 0,
												textAlign: 'left',
											}}
										>
											The image is used as a thumbnail image in the more articles column
										</Col>
									)}
									<Panel
										style={{
											cursor: 'pointer',
											width: props.article.instantArticle!.iframe ? '50%' : '100%',
										}}
									>
										<img
											src="/images/1200x628_placeholder.png"
											alt="Upload an image"
											width="100%"
										/>
									</Panel>
								</>
							)}
						</ControlLabel>
						<FormControl
							style={{
								opacity: 0,
								position: 'absolute',
								pointerEvents: 'none',
								width: '1px',
								height: '1px',
							}}
							type="file"
							placeholder="Upload image"
							onChange={setMainImage}
							data-test="image_upload"
						/>
					</Col>
				</FormGroup>
			</Row>
		)
	}

	const renderImageSizeWarning = () => {
		return (
			((imageSize.width !== undefined && imageSize.width < 1200) ||
				(imageSize.height !== undefined && imageSize.height < 628)) && (
				<div style={{ display: 'flex', marginTop: '20px' }}>
					<div
						style={{
							marginTop: 0,
							fontSize: '1.5em',
							paddingRight: '10px',
						}}
					>
						<i className="fa fa-exclamation-triangle text-danger" />
					</div>
					<div>
						<p className="text-danger">
							<b>
								WARNING: image does not follow guidelines. For instant articles, the required minimum
								image size is 1200x628.
							</b>
						</p>
						<p>
							Click on the image to select another. Read more about proper image use{' '}
							<a href="https://get.readpeak.com/proper-image/" rel="noreferrer" target="_blank">
								here.
							</a>
						</p>
					</div>
				</div>
			)
		)
	}
	const renderImageError = (forLogo?: boolean) => {
		const error = forLogo ? logoError : imageError
		return (
			error && (
				<div style={{ display: 'flex', marginTop: '20px' }}>
					<div
						style={{
							marginTop: 0,
							fontSize: '1.5em',
							paddingRight: '10px',
						}}
					>
						<i className="fa fa-exclamation-triangle text-danger" />
					</div>
					<div>
						<p className="text-danger">
							<b>
								WARNING: Error processing the image. Make sure the image is a proper image file and the
								file size does not exceed 10 MBs.
							</b>
						</p>
					</div>
				</div>
			)
		)
	}

	const validateEmbedIframe = (iframe?: string) => {
		if (!iframe || validateIframe(iframe.trim()) || iframe.trim() === '') {
			return 'success'
		}

		return 'error'
	}

	const validateIframe = (iframe: string): boolean => {
		if (!iframe) {
			return true
		}
		const pattern = new RegExp(
			// eslint-disable-next-line no-useless-escape
			'.*<iframe.*src=.*</iframe>.*',
			'i',
		)
		return !!pattern.test(iframe)
	}

	const renderEmbeddedVideo = () => {
		return editable ? (
			<Row style={{ marginTop: '15px' }}>
				<FormGroup
					controlId="main-iframe"
					validationState={validateEmbedIframe(props.article.instantArticle!.iframe)}
					style={{ margin: 0 }}
				>
					<Col md={9}>
						<ControlLabel>Iframe Embed Code</ControlLabel>
						<FormControl
							type="text"
							componentClass="textarea"
							placeholder="Insert the iframe embed code"
							value={props.article.instantArticle!.iframe}
							onChange={setVideoUrl}
							onKeyPress={(event) => {
								if (event.key === 'Enter' && toggleEditable) {
									toggleEditable()
								}
							}}
							autoFocus={focusedElement === 'main-iframe'}
						/>
					</Col>
					<Col md={3} style={{ paddingTop: '10px' }}>
						<ButtonGroup vertical>
							<Button
								style={{ borderTopLeftRadius: '3px', borderTopRightRadius: '3px' }}
								bsStyle="primary"
								onClick={() => toggleEditable()}
							>
								Add
							</Button>
							<Button
								style={{ borderBottomLeftRadius: '3px', borderBottomRightRadius: '3px' }}
								bsStyle="warning"
								onClick={() => props.updateInstantArticle({ iframe: '' })}
							>
								Remove
							</Button>
						</ButtonGroup>
					</Col>
				</FormGroup>
			</Row>
		) : props.article.instantArticle!.iframe ? (
			<>
				<Col className="embed-responsive embed-responsive-16by9">
					<iframe
						className="embed-responsive-item"
						src={extractIframeSrc(props.article.instantArticle!.iframe)}
						allowFullScreen
					></iframe>
				</Col>
			</>
		) : (
			<Row style={{ marginTop: '15px' }} onClick={() => toggleEditable('main-iframe')}>
				<FormGroup controlId={`video-${props.article.uid}`} style={{ margin: 0 }}>
					<Col xs={12}>Add an embedded video</Col>
				</FormGroup>
			</Row>
		)
	}

	const extractIframeSrc = (iframe: string) => {
		const srcSplit = iframe.split('src="')
		if (srcSplit.length > 1) {
			const srcVal = srcSplit[1].split('"')[0]
			return srcVal
		}
		return iframe
	}

	const renderReadMoreLinks = () => {
		if (props.instantArticles.articles && Object.keys(props.instantArticles.articles).length > 1) {
			return (
				<>
					<Row style={{ fontSize: '20px', marginTop: '10px' }}>More Articles By</Row>
					<Row style={{ fontSize: '20px', marginBottom: '20px' }}>
						{props.instantArticles.client_name || '(client not selected)'}
					</Row>
					{Object.keys(props.instantArticles.articles).map((key) => {
						if (key !== props.article.uid) {
							const art = props.instantArticles.articles[key]
							return (
								<div key={art.uid}>
									<Row style={{ marginBottom: '10px' }}>
										<img
											src={art.instantArticle?.image || '../images/1200x628_placeholder.png'}
											alt="readmore_img"
											width="100%"
										/>
									</Row>
									<Row style={{ fontSize: '18px', fontWeight: 'bold' }}>
										{art.instantArticle?.headline}
									</Row>
									<Row style={{ fontSize: '10px', marginBottom: '20px' }}>
										{art.instantArticle?.introduction}
									</Row>
								</div>
							)
						}
					})}
				</>
			)
		}
		return <></>
	}

	const handleLogo = (e: any) => {
		e.preventDefault()

		setLogoLoading(true)
		if (!e.target.files[0]) {
			return
		}

		const fileReader = new FileReader()
		fileReader.addEventListener('loadend', () => {
			const result = fileReader.result
			if (typeof result !== 'string') {
				return
			}
			saveLogo(result)
		})

		fileReader.readAsDataURL(e.target.files[0])
	}

	const resetValues = () => {
		setEditable(false)
		setImageError(false)
		setImageLoading(false)
		setLogoLoading(false)
	}

	const renderMainItem = () => {
		return !editable && props.article.instantArticle!.iframe ? <>{renderEmbeddedVideo()}</> : <>{renderImage()}</>
	}

	const renderMainModal = () => {
		if (props.show) {
			return (
				<div className="modal-content" style={{ borderRadius: '4px' }}>
					<div className="instant-main-modal modal-body" style={{ marginLeft: '20px', marginRight: '30px' }}>
						<Row style={{ marginBottom: '20px' }} onClick={() => toggleEditable()}>
							<Col xs={12}>
								SPONSORED ARTICLE BY {props.instantArticles.client_name || '(client not selected)'}
							</Col>
						</Row>
						<Row>
							<Col className="instant-main-left-col" sm={9}>
								{renderImageSizeWarning()}
								{renderImageError()}
								{renderMainItem()}

								<Row style={{ marginTop: '15px' }}>
									<InstantArticlesItem
										name="headline"
										type="text"
										placeholder="Create a headline for your instant article here"
										value={props.article.instantArticle!.headline}
										onChange={setHeadline}
										editable={editable}
										toggleEditable={toggleEditable}
										maxLength={InstantArticleLimits.headline}
										minLength={InstantArticleLimits.minHeadline}
										focus={focusedElement === 'headline'}
										suggestion={false}
										textElement="strong"
										textStyle={{ fontSize: '26px' }}
									/>
								</Row>

								<Row>
									<InstantArticlesItem
										name="introduction"
										type="text"
										placeholder="Add a maximum of 120 character introduction for your instant article here"
										value={props.article.instantArticle!.introduction}
										onChange={setIntroduction}
										editable={editable}
										toggleEditable={toggleEditable}
										maxLength={InstantArticleLimits.introduction}
										minLength={InstantArticleLimits.minIntroduction}
										focus={focusedElement === 'introduction'}
										suggestion={false}
										textElement="strong"
										textStyle={{ fontSize: '18px' }}
									/>
								</Row>

								<Row style={{ marginBottom: '20px' }}>
									<Col md={editable ? 11 : 12}>
										<Col
											style={{
												border: editable ? '1px solid rgb(221, 221, 221)' : undefined,
												borderRadius: '3px',
												minHeight: '200px',
											}}
										>
											<BodyEditor
												name={'body_editor'}
												html={props.article.instantArticle!.body}
												setBody={setBody}
												placeholder="This is where you tell your story. Make it interesting and engaging. You can use hyperlinks and change the font or style of your text."
												toggleEditable={toggleEditable}
												editable={editable}
												imageUploadCallback={imageUploadCallback}
											/>
										</Col>
									</Col>
									{editable && (
										<Col md={1}>
											<IconButton
												tooltipText="OK"
												onClick={toggleEditable}
												icon="fa-check text-success"
											/>
										</Col>
									)}
								</Row>
							</Col>
							<Col sm={3} style={{ paddingLeft: '40px' }}>
								{logoLoading ? (
									<LoadIndicator />
								) : (
									<>
										{renderImageError(true)}
										<Row className="logo-row">
											<Col
												xs={4}
												sm={12}
												style={
													props.instantArticles.logo
														? { paddingLeft: '0px', marginBottom: '10px' }
														: { paddingLeft: '0px', marginBottom: '10px' }
												}
												className="img-responsive center-block"
											>
												<label htmlFor="upload-logo" style={{ width: '100%' }}>
													{props.instantArticles.logo ? (
														<img
															src={props.instantArticles.logo}
															alt="preview_logo"
															width="100%"
														/>
													) : (
														<>
															<span style={{ width: '100%' }} className="btn btn-primary">
																Upload a Logo
															</span>
														</>
													)}
												</label>
												<input
													type="file"
													id="upload-logo"
													style={{ display: 'none' }}
													onChange={handleLogo}
												/>
											</Col>
										</Row>
									</>
								)}
								{renderReadMoreLinks()}
							</Col>
						</Row>
					</div>
					<div className="modal-footer">
						<Button
							bsStyle="success"
							onClick={() => {
								props.onHide()
								resetValues()
							}}
						>
							Save
						</Button>
					</div>
				</div>
			)
		}
	}

	const renderMainItemSettings = () => {
		return !editable && props.article.instantArticle!.iframe ? <>{renderImage()}</> : <>{renderEmbeddedVideo()}</>
	}

	const renderSettingsModal = () => {
		if (props.show) {
			return (
				<div className="instant-settings-modal">
					<Col style={{ marginLeft: '20px', marginRight: '30px', padding: '15px' }}>
						<Row style={{ fontWeight: 400 }}>
							<Col sm={12} style={{ paddingRight: '25px' }}>
								<h2 style={{ fontSize: '22px', marginTop: '0px' }}>Settings</h2>
							</Col>
						</Row>
						<Row style={{ marginBottom: '20px' }}>
							<Col sm={12} style={{ paddingRight: '25px' }}>
								{renderMainItemSettings()}
							</Col>
						</Row>
					</Col>
				</div>
			)
		}
	}

	return props.show ? (
		<div className="modal-background">
			<div className="ia-modal">
				<div className="left-ia-modal">{renderMainModal()}</div>
				<div className="right-ia-modal">{renderSettingsModal()}</div>
			</div>
		</div>
	) : (
		<></>
	)
}

export default InstantArticlesModal
